import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { MultiImageUploader } from './MultiImageUploader';
import { VariantImagePicker } from './VariantImagePicker';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Plus, X, Star, ChevronRight, ChevronLeft, Package, Palette, Type, Upload, FileText, Layers, Euro, FolderTree, Check, Info, Rocket, Truck, Clock, AlertTriangle, Loader2, Sparkles, TreeDeciduous, Leaf, Shield, Gift, Calendar, Hand, Fingerprint, PenTool, History, Paintbrush, Scissors, Hammer, Gem, Crown, Circle, Mountain, Coffee, Award, Heart, Recycle, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { calculateFinalPrice, getFeeBreakdown, FEE_CONSTANTS, isLaunchModeActive } from '../lib/feeCalculator';

const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Disponibilità immediata' },
  { value: '1-2days', label: '1-2 giorni' },
  { value: '3-5days', label: '3-5 giorni' },
  { value: '1week', label: '1 settimana' },
  { value: '1-2weeks', label: '1-2 settimane' },
  { value: '3-4weeks', label: '3-4 settimane' },
  { value: 'over-month', label: 'Oltre un mese' },
];

const CUSTOMIZATION_INPUT_TYPES = [
  { value: 'text', label: 'Testo libero', icon: Type, description: 'L\'acquirente scrive un testo (es: nome da incidere)' },
  { value: 'textarea', label: 'Testo lungo', icon: FileText, description: 'L\'acquirente scrive un testo più lungo' },
  { value: 'file', label: 'Upload file', icon: Upload, description: 'L\'acquirente carica un file (foto, logo, ecc.)' },
  { value: 'select', label: 'Scelta da lista', icon: Layers, description: 'L\'acquirente sceglie da opzioni predefinite' },
];

export const ProductForm = ({
  // Base product
  productTitle,
  setProductTitle,
  productDescription,
  setProductDescription,
  baseImages,
  setBaseImages,
  mainImageIndex,
  setMainImageIndex,
  basePrice,
  setBasePrice,
  crafterDiscount,
  setCrafterDiscount,
  baseTimeline,
  setBaseTimeline,
  // Commission terms for non-immediate products
  commissionTermsAccepted,
  setCommissionTermsAccepted,
  // Category
  selectedCategory,
  setSelectedCategory,
  // Categories from database
  categories = [],
  categoriesLoading = false,
  // Variants
  hasVariants,
  setHasVariants,
  variants,
  setVariants,
  // Customizations
  hasCustomizations,
  setHasCustomizations,
  customizations,
  setCustomizations,
  // Inventory (step 2)
  inventory,
  setInventory,
  // Step management
  currentStep,
  setCurrentStep,
  // Shipping Rules
  shippingRules = [],
  selectedShippingRuleId,
  setSelectedShippingRuleId,
  onConfigureShipping,
  // Highlights
  highlights = [],
  setHighlights,
}) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  // Local state for adding new variant type
  const [newVariantType, setNewVariantType] = useState('');
  const [newVariantValues, setNewVariantValues] = useState('');
  const [showVariantPrices, setShowVariantPrices] = useState(false);
  const [tempVariantPrices, setTempVariantPrices] = useState({});
  const [newValueInputs, setNewValueInputs] = useState({}); // Per aggiungere valori a varianti esistenti
  
  // Highlights state
  const [availableHighlights, setAvailableHighlights] = useState({});
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const MAX_HIGHLIGHTS = 5;
  
  // Load available highlights based on selected category
  useEffect(() => {
    const loadHighlights = async () => {
      setHighlightsLoading(true);
      try {
        // Extract main category slug from selectedCategory
        const categorySlug = selectedCategory?.main?.toLowerCase().replace(/\s+/g, '-') || '';
        const url = categorySlug 
          ? `${API_URL}/api/product-highlights?category=${categorySlug}`
          : `${API_URL}/api/product-highlights`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setAvailableHighlights(data.highlights || {});
          // Expand first group by default
          const firstGroup = Object.keys(data.highlights || {})[0];
          if (firstGroup) {
            setExpandedGroups({ [firstGroup]: true });
          }
        }
      } catch (error) {
        console.error('Error loading highlights:', error);
      } finally {
        setHighlightsLoading(false);
      }
    };
    
    loadHighlights();
  }, [API_URL, selectedCategory?.main]);
  
  // Toggle highlight selection
  const toggleHighlight = (key) => {
    if (!setHighlights) return;
    
    const current = highlights || [];
    if (current.includes(key)) {
      setHighlights(current.filter(k => k !== key));
    } else if (current.length < MAX_HIGHLIGHTS) {
      setHighlights([...current, key]);
    }
  };
  
  // Toggle group expansion
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };
  
  // Icon mapping for highlights
  const getHighlightIcon = (iconName) => {
    const icons = {
      TreeDeciduous, Leaf, Shield, Gift, Calendar, Hand, Fingerprint, PenTool, 
      History, Paintbrush, Scissors, Hammer, Gem, Crown, Circle, Mountain, 
      Coffee, Award, Heart, Recycle, MapPin, Layers, Star, Sparkles, Package
    };
    return icons[iconName] || Star;
  };
  
  // Category selection state
  const [categoryLevel, setCategoryLevel] = useState(0); // 0=main, 1=sub, 2=child
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  
  // Local state for adding new customization
  const [newCustomization, setNewCustomization] = useState({
    name: '',
    inputType: 'text',
    options: '', // For 'select' type
    required: false,
    priceIncrease: '',
  });

  // Category navigation handlers
  const handleSelectMainCategory = (category) => {
    setSelectedMainCategory(category);
    setSelectedSubCategory(null);
    setCategoryLevel(1);
  };

  const handleSelectSubCategory = (subcat) => {
    setSelectedSubCategory(subcat);
    setCategoryLevel(2);
  };

  const handleSelectChildCategory = (child) => {
    // Build the full category path. `child` può essere oggetto {name, slug} o stringa;
    // salviamo SEMPRE una stringa in categoryPath.child (usata come label nei badge).
    const childName = typeof child === 'object' && child !== null ? (child.name || child.slug) : child;
    const categoryPath = {
      main: selectedMainCategory.id,
      mainName: selectedMainCategory.name,
      sub: selectedSubCategory.name,
      child: childName,
      childSlug: typeof child === 'object' && child !== null ? child.slug : undefined,
    };
    setSelectedCategory(categoryPath);
    // Reset navigation
    setCategoryLevel(0);
    setSelectedMainCategory(null);
    setSelectedSubCategory(null);
  };

  const handleResetCategory = () => {
    setSelectedCategory(null);
    setCategoryLevel(0);
    setSelectedMainCategory(null);
    setSelectedSubCategory(null);
  };

  // Variant handlers
  const handleAddVariant = () => {
    if (!newVariantType.trim() || !newVariantValues.trim()) return;
    
    const values = newVariantValues.split(',').map(v => v.trim()).filter(v => v);
    if (values.length === 0) return;
    
    // Create variant with price structure
    const newVariant = {
      type: newVariantType.trim(),
      values: values,
      prices: values.reduce((acc, val) => ({ ...acc, [val]: tempVariantPrices[val] || 0 }), {})
    };
    
    setVariants([...variants, newVariant]);
    setNewVariantType('');
    setNewVariantValues('');
    setTempVariantPrices({});
    setShowVariantPrices(false);
  };

  const handleRemoveVariant = (index) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const handleUpdateVariantPrice = (variantIndex, value, price) => {
    const updatedVariants = [...variants];
    if (!updatedVariants[variantIndex].prices) {
      updatedVariants[variantIndex].prices = {};
    }
    updatedVariants[variantIndex].prices[value] = parseFloat(price) || 0;
    setVariants(updatedVariants);
  };

  const handleUpdateVariantImage = (variantIndex, value, imageUrl) => {
    const updatedVariants = [...variants];
    if (!updatedVariants[variantIndex].images) {
      updatedVariants[variantIndex].images = {};
    }
    if (imageUrl) {
      updatedVariants[variantIndex].images[value] = imageUrl;
    } else {
      delete updatedVariants[variantIndex].images[value];
    }
    setVariants(updatedVariants);
  };

  const handleAddValuesToVariant = (variantIndex, rawInput) => {
    const parsed = rawInput.split(',').map(v => v.trim()).filter(v => v);
    if (parsed.length === 0) return;

    const updatedVariants = [...variants];
    const target = { ...updatedVariants[variantIndex] };
    const existing = new Set(target.values || []);
    const toAdd = parsed.filter(v => !existing.has(v));
    if (toAdd.length === 0) return;

    target.values = [...(target.values || []), ...toAdd];
    target.prices = { ...(target.prices || {}) };
    toAdd.forEach(v => {
      if (target.prices[v] === undefined) target.prices[v] = 0;
    });
    updatedVariants[variantIndex] = target;
    setVariants(updatedVariants);
  };

  const handleRemoveValueFromVariant = (variantIndex, value) => {
    const updatedVariants = [...variants];
    const target = { ...updatedVariants[variantIndex] };
    target.values = (target.values || []).filter(v => v !== value);
    if (target.prices) {
      const newPrices = { ...target.prices };
      delete newPrices[value];
      target.prices = newPrices;
    }
    if (target.images) {
      const newImages = { ...target.images };
      delete newImages[value];
      target.images = newImages;
    }
    // Se la variante rimane senza valori, rimuovi la variante intera
    if (target.values.length === 0) {
      updatedVariants.splice(variantIndex, 1);
    } else {
      updatedVariants[variantIndex] = target;
    }
    setVariants(updatedVariants);
  };

  const parseTempVariantValues = () => {
    return newVariantValues.split(',').map(v => v.trim()).filter(v => v);
  };

  // Customization handlers
  const handleAddCustomization = () => {
    if (!newCustomization.name.trim()) return;
    
    const customToAdd = {
      ...newCustomization,
      options: newCustomization.inputType === 'select' 
        ? newCustomization.options.split(',').map(o => o.trim()).filter(o => o)
        : [],
      priceIncrease: parseFloat(newCustomization.priceIncrease) || 0,
    };
    
    setCustomizations([...customizations, customToAdd]);
    setNewCustomization({
      name: '',
      inputType: 'text',
      options: '',
      required: false,
      priceIncrease: '',
    });
  };

  const handleRemoveCustomization = (index) => {
    setCustomizations(customizations.filter((_, i) => i !== index));
  };

  // Generate inventory combinations
  const getInventoryKeys = () => {
    if (!hasVariants || variants.length === 0) {
      return ['default'];
    }
    
    // Generate all combinations of variants
    const combinations = variants.reduce((acc, variant) => {
      if (acc.length === 0) {
        return variant.values.map(v => ({ [variant.type]: v }));
      }
      const newCombinations = [];
      acc.forEach(combo => {
        variant.values.forEach(v => {
          newCombinations.push({ ...combo, [variant.type]: v });
        });
      });
      return newCombinations;
    }, []);
    
    return combinations.map(combo => 
      Object.entries(combo).map(([k, v]) => `${k}:${v}`).join('|')
    );
  };

  const formatInventoryKey = (key) => {
    if (key === 'default') return 'Prodotto base';
    return key.split('|').map(part => {
      const [type, value] = part.split(':');
      return `${type}: ${value}`;
    }).join(', ');
  };

  // Check if step 2 is needed
  const needsStep2 = baseTimeline === 'immediate';

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Banner: nessuna regola di spedizione configurata */}
      {shippingRules.length === 0 && onConfigureShipping && (
        <div
          className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg"
          data-testid="no-shipping-rules-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                Configura prima una regola di spedizione
              </p>
              <p className="text-sm text-amber-800 mt-1">
                Prima di creare il tuo primo prodotto, devi definire almeno una regola di spedizione.
                Verrai riportato qui dopo averla creata.
              </p>
              <Button
                type="button"
                size="sm"
                className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => {
                  sessionStorage.setItem('pending_product_creation', '1');
                  onConfigureShipping();
                }}
                data-testid="configure-shipping-top-cta"
              >
                <Truck className="w-4 h-4 mr-2" />
                Vai a Regole di Spedizione
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step indicator */}
      {needsStep2 && (
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${currentStep === 1 ? 'bg-[#008080] text-white' : 'bg-slate-100 text-slate-600'}`}>
            <span className="font-medium">1</span>
            <span className="text-sm">Dettagli prodotto</span>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${currentStep === 2 ? 'bg-[#008080] text-white' : 'bg-slate-100 text-slate-600'}`}>
            <span className="font-medium">2</span>
            <span className="text-sm">Disponibilità magazzino</span>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <>
          {/* PRODOTTO BASE */}
          <Card className="p-6 bg-slate-50 border-2 border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-[#008080]" />
              <h4 className="font-bold text-xl text-[#008080]">Prodotto Base</h4>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Titolo *</Label>
                <Input
                  value={productTitle}
                  onChange={(e) => setProductTitle(e.target.value)}
                  placeholder="Es: Tazza in Ceramica Artigianale"
                  required
                />
              </div>
              
              <div>
                <Label>Descrizione</Label>
                <Textarea
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  placeholder="Descrivi il prodotto, i materiali, le caratteristiche..."
                  rows={4}
                />
              </div>

              {/* Categoria */}
              <div>
                <Label className="flex items-center gap-2">
                  <FolderTree className="w-4 h-4" />
                  Categoria *
                </Label>
                
                {selectedCategory ? (
                  // Selected category display
                  <div className="mt-2 p-3 bg-white rounded-lg border-2 border-[#008080] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-[#008080]" />
                      <div>
                        <span className="text-sm text-slate-500">{selectedCategory.mainName}</span>
                        <span className="text-slate-400 mx-1">›</span>
                        <span className="text-sm text-slate-500">{selectedCategory.sub}</span>
                        <span className="text-slate-400 mx-1">›</span>
                        <span className="font-medium text-[#008080]">{typeof selectedCategory.child === 'object' ? selectedCategory.child.name : selectedCategory.child}</span>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleResetCategory}
                      className="text-slate-500 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  // Category navigator
                  <div className="mt-2 p-4 bg-white rounded-lg border-2 border-slate-200">
                    {/* Breadcrumb */}
                    {categoryLevel > 0 && (
                      <div className="flex items-center gap-2 text-sm mb-3 pb-3 border-b">
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryLevel(0);
                            setSelectedMainCategory(null);
                            setSelectedSubCategory(null);
                          }}
                          className="text-[#008080] hover:underline"
                        >
                          Tutte le categorie
                        </button>
                        {selectedMainCategory && (
                          <>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                            <button
                              type="button"
                              onClick={() => {
                                setCategoryLevel(1);
                                setSelectedSubCategory(null);
                              }}
                              className={categoryLevel === 1 ? 'font-medium' : 'text-[#008080] hover:underline'}
                            >
                              {selectedMainCategory.name}
                            </button>
                          </>
                        )}
                        {selectedSubCategory && (
                          <>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{selectedSubCategory.name}</span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Level 0: Main categories */}
                    {categoryLevel === 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {categoriesLoading ? (
                          <div className="col-span-2 flex items-center justify-center py-8 text-slate-500">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Caricamento categorie...
                          </div>
                        ) : categories.length === 0 ? (
                          <div className="col-span-2 text-center py-8 text-slate-500">
                            Nessuna categoria disponibile
                          </div>
                        ) : (
                          categories.map((cat) => {
                            const IconComponent = cat.icon;
                            return (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => handleSelectMainCategory(cat)}
                                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#008080] hover:bg-[#008080]/5 transition-colors text-left"
                              >
                                <IconComponent className="w-5 h-5 text-[#008080]" />
                                <span className="text-sm font-medium">{cat.name}</span>
                                <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}

                    {/* Level 1: Subcategories */}
                    {categoryLevel === 1 && selectedMainCategory && (
                      <div className="grid grid-cols-2 gap-2">
                        {selectedMainCategory.subcategories.map((subcat) => {
                          const IconComponent = subcat.icon;
                          return (
                            <button
                              key={subcat.name}
                              type="button"
                              onClick={() => handleSelectSubCategory(subcat)}
                              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#008080] hover:bg-[#008080]/5 transition-colors text-left"
                            >
                              <IconComponent className="w-5 h-5 text-slate-500" />
                              <span className="text-sm font-medium">{subcat.name}</span>
                              <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Level 2: Children (final selection) */}
                    {categoryLevel === 2 && selectedSubCategory && (
                      <div className="grid grid-cols-2 gap-2">
                        {selectedSubCategory.children.map((child) => (
                          <button
                            key={child.slug || child.name || child}
                            type="button"
                            onClick={() => handleSelectChildCategory(child)}
                            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#008080] hover:bg-[#008080]/5 transition-colors text-left"
                          >
                            <Check className="w-5 h-5 text-transparent" />
                            <span className="text-sm font-medium">{typeof child === 'object' ? child.name : child}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-slate-500 mt-3">
                      {categoryLevel === 0 && 'Seleziona una categoria principale'}
                      {categoryLevel === 1 && 'Seleziona una sottocategoria'}
                      {categoryLevel === 2 && 'Seleziona la categoria specifica per il tuo prodotto'}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Immagini */}
              <MultiImageUploader
                images={baseImages}
                setImages={setBaseImages}
                maxImages={7}
                label="Immagini prodotto"
                bucketName="products"
                mainImageIndex={mainImageIndex}
                setMainImageIndex={setMainImageIndex}
                showMainSelector={true}
              />

              {/* Prezzo e Sconto */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Prezzo (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={basePrice}
                      onChange={(e) => setBasePrice(e.target.value)}
                      placeholder="25.00"
                      required
                    />
                  </div>
                  <div>
                    <Label>Sconto artigiano (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="90"
                      step="1"
                      value={crafterDiscount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 90)) {
                          setCrafterDiscount(val);
                        }
                      }}
                      placeholder="0"
                    />
                    <p className="text-xs text-slate-500 mt-1">Opzionale. Sconto applicato al cliente (max 90%)</p>
                  </div>
                </div>
                
                {/* Launch mode banner */}
                {isLaunchModeActive() && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Rocket className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-green-800">
                        <p className="font-semibold">🎉 Promo Lancio Rarifex!</p>
                        <p>La commissione dell'8% di Rarifex è attualmente azzerata. Riceverai l'intero importo delle vendite, al netto solo delle commissioni del sistema di pagamento Stripe (~1,5% + €0,25 per transazione).</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Fee explanation and calculation - NEW MODEL */}
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-start gap-2 mb-2">
                    <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-slate-600">
                      Inserisci il <strong>prezzo finale</strong> che vedrà il cliente.
                      {isLaunchModeActive() 
                        ? ' Durante la promo lancio, riceverai il 100% del prezzo!'
                        : ` La commissione Rarifex (${FEE_CONSTANTS.PERCENTAGE_DISPLAY} onnicomprensiva, minimo ${FEE_CONSTANTS.MIN_FEE_DISPLAY} sugli ordini piccoli, include costi di piattaforma e pagamento) verrà trattenuta in caso di vendita.`
                      }
                    </p>
                  </div>
                  
                  {basePrice && parseFloat(basePrice) > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                      {(() => {
                        const discount = crafterDiscount ? parseFloat(crafterDiscount) : 0;
                        const originalPrice = parseFloat(basePrice);
                        const discountedPrice = originalPrice * (1 - discount / 100);
                        const breakdown = getFeeBreakdown(discountedPrice);
                        
                        return (
                          <>
                            {/* Customer sees */}
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-slate-700">Prezzo per il cliente:</span>
                              <span className="text-lg font-bold text-[#008080]">
                                €{discount > 0 ? discountedPrice.toFixed(2) : originalPrice.toFixed(2)}
                              </span>
                            </div>
                            
                            {discount > 0 && (
                              <div className="flex justify-between text-xs text-orange-600">
                                <span>Prezzo originale (senza tuo sconto):</span>
                                <span className="line-through">€{originalPrice.toFixed(2)}</span>
                              </div>
                            )}
                            
                            {/* Fee breakdown */}
                            <div className="mt-2 p-2 bg-white rounded border border-slate-100">
                              <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Calcolo del tuo guadagno</p>
                              
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between text-slate-600">
                                  <span>Prezzo vendita:</span>
                                  <span>€{(discount > 0 ? discountedPrice : originalPrice).toFixed(2)}</span>
                                </div>
                                
                                <div className="flex justify-between text-slate-500">
                                  <span>
                                    Commissione Rarifex ({FEE_CONSTANTS.PERCENTAGE_DISPLAY})
                                    <span className="text-[10px] text-slate-400 block">incl. costi piattaforma e pagamento</span>
                                  </span>
                                  {breakdown.isLaunchMode ? (
                                    <span className="text-green-600 font-semibold">€0,00 ✓</span>
                                  ) : (
                                    <span className="text-red-500">-€{breakdown.feeAmount.toFixed(2)}</span>
                                  )}
                                </div>
                                
                                <div className="flex justify-between font-semibold text-[#008080] pt-1.5 border-t border-slate-100">
                                  <span>💰 Tu riceverai:</span>
                                  <span className="text-base">€{breakdown.crafterEarnings.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                            
                            {breakdown.isLaunchMode && (
                              <p className="text-[10px] text-green-600 text-center">
                                🎉 Promo attiva: commissione Rarifex azzerata (al netto delle sole fee Stripe).
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Disponibilità / Tempi di realizzazione */}
              <div className="space-y-3">
                <div>
                  <Label>Disponibilità / Tempi di realizzazione *</Label>
                  <Select value={baseTimeline} onValueChange={setBaseTimeline}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMELINE_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Avviso per prodotti su commissione non personalizzati */}
                {baseTimeline && baseTimeline !== 'immediate' && (
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="space-y-2">
                        <h4 className="font-semibold text-amber-800">Prodotto su Commissione</h4>
                        <p className="text-sm text-amber-700">
                          Hai selezionato un tempo di realizzazione diverso da "Disponibilità immediata". 
                          Questo significa che il prodotto verrà <strong>creato su richiesta</strong> quando viene ordinato.
                        </p>
                        <div className="bg-white/70 rounded-md p-3 text-sm text-slate-700 space-y-2">
                          <p className="font-medium text-slate-800">⚠️ Importante - Diritti dell'acquirente:</p>
                          <ul className="list-disc list-inside space-y-1 text-slate-600">
                            <li>Se stai creando un prodotto <strong>non personalizzabile</strong>, manterrà tutte le caratteristiche di un prodotto a magazzino</li>
                            <li>L'acquirente ha diritto alla <strong>restituzione entro 14 giorni</strong> dalla consegna</li>
                            <li>L'acquirente può esercitare il <strong>diritto di ripensamento</strong> nel periodo tra l'acquisto e la realizzazione del prodotto</li>
                            <li>Se invece configurerai la voce <strong>Personalizzazioni</strong> allora non si applicheranno i diritti di ripensamento entro 14 giorni</li>
                          </ul>
                          <p className="text-amber-700 font-medium pt-2">
                            💡 Consiglio: configura i tempi di realizzazione in modo adeguato per bilanciare le tue esigenze produttive con i diritti dell'acquirente.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Checkbox accettazione termini */}
                    <div className="flex items-start gap-3 pt-2 border-t border-amber-200">
                      <input
                        type="checkbox"
                        id="commission-terms-accept"
                        checked={commissionTermsAccepted}
                        onChange={(e) => setCommissionTermsAccepted(e.target.checked)}
                        className="mt-1 h-4 w-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                      />
                      <label htmlFor="commission-terms-accept" className="text-sm text-slate-700">
                        <span className="font-medium">Accetto i termini per i prodotti su commissione non personalizzati.</span>
                        <br />
                        <span className="text-slate-500">
                          Dichiaro di aver compreso che l'acquirente mantiene il diritto di recesso e restituzione 
                          come previsto per i prodotti standard a catalogo.
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* PUNTI DI FORZA */}
          <Card className="p-6 bg-amber-50 border-2 border-amber-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <h4 className="font-bold text-xl text-amber-700">Punti di Forza</h4>
              </div>
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                {(highlights || []).length}/{MAX_HIGHLIGHTS}
              </Badge>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Seleziona fino a {MAX_HIGHLIGHTS} caratteristiche che descrivono al meglio il tuo prodotto. Appariranno nella scheda prodotto.
            </p>
            
            {/* Selected highlights */}
            {(highlights || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 p-3 bg-white rounded-lg border border-amber-200">
                {(highlights || []).map(key => {
                  // Find highlight info
                  let highlightInfo = null;
                  for (const group of Object.values(availableHighlights)) {
                    const found = group.items?.find(h => h.key === key);
                    if (found) {
                      highlightInfo = found;
                      break;
                    }
                  }
                  if (!highlightInfo) return null;
                  
                  const IconComp = getHighlightIcon(highlightInfo.icon);
                  return (
                    <Badge 
                      key={key}
                      className="bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200 cursor-pointer flex items-center gap-1.5 px-3 py-1.5"
                      onClick={() => toggleHighlight(key)}
                    >
                      <IconComp className="w-3.5 h-3.5" />
                      {highlightInfo.label}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  );
                })}
              </div>
            )}
            
            {/* Available highlights grouped */}
            {highlightsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(availableHighlights).map(([groupKey, group]) => (
                  <div key={groupKey} className="border border-amber-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleGroup(groupKey)}
                      className="w-full flex items-center justify-between p-3 bg-white hover:bg-amber-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {(() => {
                          const GroupIcon = getHighlightIcon(group.info?.icon);
                          return <GroupIcon className="w-4 h-4 text-amber-600" />;
                        })()}
                        <span className="font-medium text-slate-700">{group.info?.label || groupKey}</span>
                        <span className="text-xs text-slate-400">({group.items?.length || 0})</span>
                      </div>
                      {expandedGroups[groupKey] ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                    
                    {expandedGroups[groupKey] && (
                      <div className="p-3 bg-slate-50 border-t border-amber-100">
                        <div className="flex flex-wrap gap-2">
                          {(group.items || []).map(highlight => {
                            const isSelected = (highlights || []).includes(highlight.key);
                            const isDisabled = !isSelected && (highlights || []).length >= MAX_HIGHLIGHTS;
                            const IconComp = getHighlightIcon(highlight.icon);
                            
                            return (
                              <button
                                key={highlight.key}
                                type="button"
                                onClick={() => !isDisabled && toggleHighlight(highlight.key)}
                                disabled={isDisabled}
                                className={`
                                  flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all
                                  ${isSelected 
                                    ? 'bg-amber-500 text-white shadow-sm' 
                                    : isDisabled
                                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                      : 'bg-white text-slate-700 border border-slate-200 hover:border-amber-300 hover:bg-amber-50'
                                  }
                                `}
                                title={highlight.description}
                              >
                                <IconComp className="w-3.5 h-3.5" />
                                {highlight.label}
                                {isSelected && <Check className="w-3 h-3 ml-0.5" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* VARIANTI */}
          <Card className="p-6 bg-blue-50 border-2 border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-600" />
                <h4 className="font-bold text-xl text-blue-700">Varianti</h4>
              </div>
              <Switch
                checked={hasVariants}
                onCheckedChange={setHasVariants}
              />
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Attiva se il prodotto ha varianti come taglia, colore, materiale, ecc.
            </p>

            {hasVariants && (
              <div className="space-y-4">
                {/* Existing variants list - show first if any */}
                {variants.length > 0 && (
                  <div className="space-y-3 mb-4">
                    <p className="text-sm font-medium text-blue-700">Varianti salvate ({variants.length})</p>
                    {variants.map((variant, index) => (
                      <div key={index} className="p-4 bg-white rounded-lg border border-blue-200">
                        <div className="flex items-start justify-between mb-2">
                          <span className="font-semibold text-blue-700">{variant.type}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveVariant(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 -mt-1 -mr-2"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {variant.values.map((val, idx) => {
                            const price = variant.prices?.[val] || 0;
                            return (
                              <div key={idx} className="flex items-center gap-1">
                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 pr-1">
                                  {val}
                                  {price > 0 && (
                                    <span className="ml-1 text-green-600 font-semibold">+€{price}</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveValueFromVariant(index, val)}
                                    className="ml-1.5 w-4 h-4 rounded-full hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors"
                                    title={`Rimuovi ${val}`}
                                    data-testid={`remove-variant-value-${variant.type}-${val}`}
                                  >
                                    <X className="w-2.5 h-2.5" />
                                  </button>
                                </Badge>
                              </div>
                            );
                          })}
                        </div>
                        {/* Aggiungi nuovi valori a questa variante */}
                        <div className="mt-3 flex items-center gap-2">
                          <Input
                            value={newValueInputs[index] || ''}
                            onChange={(e) => setNewValueInputs(prev => ({ ...prev, [index]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddValuesToVariant(index, newValueInputs[index] || '');
                                setNewValueInputs(prev => ({ ...prev, [index]: '' }));
                              }
                            }}
                            placeholder={`Aggiungi valori a "${variant.type}" (es: Verde, Giallo)`}
                            className="h-8 text-sm"
                            data-testid={`add-variant-value-input-${variant.type}`}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!(newValueInputs[index] || '').trim()}
                            onClick={() => {
                              handleAddValuesToVariant(index, newValueInputs[index] || '');
                              setNewValueInputs(prev => ({ ...prev, [index]: '' }));
                            }}
                            className="border-blue-400 text-blue-700 hover:bg-blue-50 h-8"
                            data-testid={`add-variant-value-btn-${variant.type}`}
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Aggiungi
                          </Button>
                        </div>
                        {/* Inline price + image editing */}
                        <div className="mt-3 pt-3 border-t border-blue-100">
                          <p className="text-xs text-slate-500 mb-2">Modifica prezzi extra e associa un&apos;immagine a ciascun valore:</p>
                          <div className="space-y-2">
                            {variant.values.map((val, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-md">
                                <VariantImagePicker
                                  value={variant.images?.[val] || null}
                                  onChange={(url) => handleUpdateVariantImage(index, val, url)}
                                  baseImages={baseImages}
                                  onBaseImagesChange={setBaseImages}
                                  bucketName="products"
                                  variantLabel={val}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-700 truncate">{val}</p>
                                  <p className="text-xs text-slate-500">
                                    {variant.images?.[val] ? 'Immagine associata' : 'Nessuna immagine'}
                                  </p>
                                </div>
                                <div className="relative flex-shrink-0">
                                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">+€</span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={variant.prices?.[val] || ''}
                                    onChange={(e) => handleUpdateVariantPrice(index, val, e.target.value)}
                                    className="w-24 pl-6 h-8 text-xs"
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new variant */}
                <div className="p-4 bg-white rounded-lg border border-blue-200 space-y-4">
                  <p className="text-sm font-medium text-slate-700">
                    {variants.length > 0 ? 'Aggiungi un\'altra variante' : 'Crea una nuova variante'}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Tipo di variante</Label>
                      <Input
                        value={newVariantType}
                        onChange={(e) => setNewVariantType(e.target.value)}
                        placeholder="Es: Taglia, Colore, Materiale"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Valori (separati da virgola)</Label>
                      <Input
                        value={newVariantValues}
                        onChange={(e) => {
                          setNewVariantValues(e.target.value);
                          setShowVariantPrices(false);
                        }}
                        placeholder="Es: S, M, L, XL"
                      />
                    </div>
                  </div>

                  {/* Price adjustment toggle */}
                  {newVariantValues.trim() && (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={showVariantPrices}
                        onCheckedChange={setShowVariantPrices}
                        id="variant-prices-toggle"
                      />
                      <Label htmlFor="variant-prices-toggle" className="text-sm cursor-pointer">
                        Aggiungi costo extra per alcune varianti
                      </Label>
                    </div>
                  )}

                  {/* Price inputs for each variant value */}
                  {showVariantPrices && parseTempVariantValues().length > 0 && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-slate-600 mb-3">Imposta un costo aggiuntivo per ogni valore (lascia 0 se non c'è extra)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {parseTempVariantValues().map((val, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-sm font-medium text-blue-700 min-w-[60px]">{val}:</span>
                            <div className="relative flex-1">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">+€</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={tempVariantPrices[val] || ''}
                                onChange={(e) => setTempVariantPrices(prev => ({
                                  ...prev,
                                  [val]: e.target.value
                                }))}
                                placeholder="0"
                                className="pl-8 h-8 text-sm"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddVariant}
                    disabled={!newVariantType.trim() || !newVariantValues.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Salva variante
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* PERSONALIZZAZIONI */}
          <Card className="p-6 bg-green-50 border-2 border-green-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-green-600" />
                <h4 className="font-bold text-xl text-green-700">Personalizzazioni</h4>
              </div>
              <Switch
                checked={hasCustomizations}
                onCheckedChange={setHasCustomizations}
              />
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Attiva se l'acquirente può personalizzare il prodotto (incisioni, stampe, modifiche su richiesta, ecc.)
            </p>

            {hasCustomizations && (
              <div className="space-y-4">
                {/* Existing customizations - show first if any */}
                {customizations.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <p className="text-sm font-medium text-green-700">Personalizzazioni salvate ({customizations.length})</p>
                    {customizations.map((custom, index) => {
                      const inputType = CUSTOMIZATION_INPUT_TYPES.find(t => t.value === custom.inputType);
                      const IconComp = inputType?.icon || Type;
                      return (
                        <div key={index} className="flex items-start justify-between p-3 bg-white rounded-lg border border-green-200">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <IconComp className="w-4 h-4 text-green-600" />
                              <span className="font-medium text-green-700">{custom.name}</span>
                              {custom.required && (
                                <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">Obbligatoria</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-slate-500">
                              <span>{inputType?.label}</span>
                              {custom.priceIncrease > 0 && (
                                <Badge className="bg-green-100 text-green-700">+€{custom.priceIncrease}</Badge>
                              )}
                            </div>
                            {custom.inputType === 'select' && custom.options?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {custom.options.map((opt, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">{opt}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveCustomization(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add new customization */}
                <div className="p-4 bg-white rounded-lg border border-green-200 space-y-4">
                  <p className="text-sm font-medium text-slate-700">
                    {customizations.length > 0 ? 'Aggiungi un\'altra personalizzazione' : 'Crea una nuova personalizzazione'}
                  </p>
                  <div>
                    <Label className="text-sm">Nome personalizzazione *</Label>
                    <Input
                      value={newCustomization.name}
                      onChange={(e) => setNewCustomization({...newCustomization, name: e.target.value})}
                      placeholder="Es: Incisione nome, Stampa logo, Colore personalizzato"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-2 block">Come l'acquirente descrive la personalizzazione?</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {CUSTOMIZATION_INPUT_TYPES.map((type) => {
                        const IconComp = type.icon;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setNewCustomization({...newCustomization, inputType: type.value})}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              newCustomization.inputType === type.value
                                ? 'border-green-500 bg-green-50'
                                : 'border-slate-200 hover:border-green-300'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <IconComp className={`w-4 h-4 ${newCustomization.inputType === type.value ? 'text-green-600' : 'text-slate-500'}`} />
                              <span className="font-medium text-sm">{type.label}</span>
                            </div>
                            <p className="text-xs text-slate-500">{type.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {newCustomization.inputType === 'select' && (
                    <div>
                      <Label className="text-sm">Opzioni disponibili (separate da virgola)</Label>
                      <Input
                        value={newCustomization.options}
                        onChange={(e) => setNewCustomization({...newCustomization, options: e.target.value})}
                        placeholder="Es: Rosso, Blu, Verde, Nero"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Costo aggiuntivo (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newCustomization.priceIncrease}
                        onChange={(e) => setNewCustomization({...newCustomization, priceIncrease: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch
                          checked={newCustomization.required}
                          onCheckedChange={(checked) => setNewCustomization({...newCustomization, required: checked})}
                        />
                        <span className="text-sm">Obbligatoria</span>
                      </label>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddCustomization}
                    disabled={!newCustomization.name.trim()}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Salva personalizzazione
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* REGOLA DI SPEDIZIONE */}
          <Card className="p-6 bg-teal-50 border-2 border-teal-200">
            <div className="flex items-center gap-2 mb-4">
              <Truck className="w-5 h-5 text-[#008080]" />
              <h4 className="font-bold text-xl text-[#008080]">Regola di Spedizione</h4>
            </div>
            
            {shippingRules.length === 0 ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Nessuna regola di spedizione configurata</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Prima di pubblicare prodotti, devi creare almeno una regola di spedizione.
                    </p>
                    {onConfigureShipping && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-2 border-amber-400 text-amber-700 hover:bg-amber-100"
                        onClick={() => {
                          sessionStorage.setItem('pending_product_creation', '1');
                          onConfigureShipping();
                        }}
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Configura Spedizioni
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Select 
                  value={selectedShippingRuleId || ''} 
                  onValueChange={setSelectedShippingRuleId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona regola di spedizione" />
                  </SelectTrigger>
                  <SelectContent>
                    {shippingRules.filter(r => r.is_active).map((rule) => (
                      <SelectItem key={rule.id} value={rule.id}>
                        <span className="flex items-center gap-2">
                          {rule.name}
                          {rule.is_default && (
                            <Badge variant="outline" className="text-xs">Predefinita</Badge>
                          )}
                          <span className="text-slate-500 text-xs">€{rule.price.toFixed(2)}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedShippingRuleId && (() => {
                  const rule = shippingRules.find(r => r.id === selectedShippingRuleId);
                  if (!rule) return null;
                  return (
                    <div className="text-sm text-slate-600 flex flex-wrap gap-3">
                      <span className="flex items-center gap-1">
                        <Euro className="w-3 h-3" /> €{rule.price.toFixed(2)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {rule.delivery_days_min}-{rule.delivery_days_max} giorni
                      </span>
                      {rule.free_shipping_threshold && (
                        <span className="flex items-center gap-1 text-green-600">
                          Gratis sopra €{rule.free_shipping_threshold}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </Card>
        </>
      )}

      {/* STEP 2: INVENTORY (only if immediate availability) */}
      {currentStep === 2 && needsStep2 && (
        <Card className="p-6 bg-amber-50 border-2 border-amber-200">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-amber-600" />
            <h4 className="font-bold text-xl text-amber-700">Disponibilità a Magazzino</h4>
          </div>
          
          <p className="text-sm text-slate-600 mb-6">
            Hai selezionato "Disponibilità immediata". Indica le quantità disponibili per ogni variante del prodotto.
          </p>

          <div className="space-y-3">
            {getInventoryKeys().map((key) => (
              <div key={key} className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200">
                <span className="font-medium text-slate-700">
                  {formatInventoryKey(key)}
                </span>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-500">Quantità:</Label>
                  <Input
                    type="number"
                    min="0"
                    className="w-24"
                    value={inventory[key] || ''}
                    onChange={(e) => setInventory({...inventory, [key]: parseInt(e.target.value) || 0})}
                    placeholder="0"
                  />
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-slate-500 mt-4">
            Lascia 0 se il prodotto è temporaneamente non disponibile. Potrai aggiornare le quantità in qualsiasi momento.
          </p>
        </Card>
      )}

      {/* Navigation buttons for step 2 */}
      {needsStep2 && (
        <div className="flex justify-between pt-4 border-t">
          {currentStep === 2 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentStep(1)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Indietro
            </Button>
          ) : (
            <div></div>
          )}
          {currentStep === 1 && (
            <Button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="bg-[#008080] text-white"
            >
              Avanti: Magazzino
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
